import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { portalGet, portalPost } from '../../lib/portalApi';
import { formatISTDateTime } from '../../lib/format';

const STATUS_COLOR = {
  pending: 'bg-amber-100 text-amber-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
};

const GRADES = ['M15', 'M20', 'M25', 'M30', 'M35', 'M40', 'M45', 'M50'];

function Field({ label, children }) {
  return <div><label className="label">{label}</label>{children}</div>;
}

export default function PortalRequest() {
  const { account } = useOutletContext();
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({
    grade: 'M25', qty_m3: '', requested_date: '', site_address: '', jobsite: '',
    contact_name: account?.contact_name || '', contact_phone: account?.phone || '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const loadHistory = () => portalGet('/portal/order-requests').then((d) => setHistory(d.items || [])).catch(() => {});
  useEffect(() => { loadHistory(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!form.qty_m3 || Number(form.qty_m3) <= 0) return toast.error('Enter a valid quantity');
    if (!form.requested_date) return toast.error('Select a requested date');
    if (!form.site_address.trim()) return toast.error('Site address is required');
    setSaving(true);
    try {
      const res = await portalPost('/portal/order-requests', { ...form, qty_m3: Number(form.qty_m3) });
      toast.success(`${res.request_no} submitted — ELR will confirm shortly`);
      setForm({ ...form, qty_m3: '', requested_date: '', site_address: '', jobsite: '', notes: '' });
      loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to submit request'); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div>
        <h1 className="font-display text-xl font-extrabold text-slate-900 mb-1">Request a New Order</h1>
        <p className="text-sm text-slate-500 mb-4">Submit your requirement — an ELR representative will confirm the schedule and rate.</p>
        <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Grade">
              <select value={form.grade} onChange={set('grade')} className="input" data-testid="portal-req-grade">
                {GRADES.map((g) => <option key={g}>{g}</option>)}
              </select>
            </Field>
            <Field label="Quantity (m³)"><input type="number" value={form.qty_m3} onChange={set('qty_m3')} className="input" data-testid="portal-req-qty" /></Field>
          </div>
          <Field label="Requested Date"><input type="date" value={form.requested_date} onChange={set('requested_date')} className="input" data-testid="portal-req-date" /></Field>
          <Field label="Site Address"><input value={form.site_address} onChange={set('site_address')} className="input" placeholder="Plot / street, area, city" data-testid="portal-req-address" /></Field>
          <Field label="Site / Project Name (optional)"><input value={form.jobsite} onChange={set('jobsite')} className="input" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Site Contact"><input value={form.contact_name} onChange={set('contact_name')} className="input" /></Field>
            <Field label="Contact Phone"><input value={form.contact_phone} onChange={set('contact_phone')} className="input" /></Field>
          </div>
          <Field label="Notes"><input value={form.notes} onChange={set('notes')} className="input" placeholder="Pump required, slump, access notes…" /></Field>
          <button onClick={submit} disabled={saving} className="btn-primary w-full justify-center mt-1" data-testid="portal-req-submit">
            {saving ? <Loader2 size={14} className="animate-spin" /> : 'Submit Request'}
          </button>
        </div>
      </div>
      <div>
        <h2 className="font-display text-sm font-bold text-slate-700 uppercase tracking-wide mb-3">Your Requests</h2>
        <div className="space-y-2">
          {history.length === 0 && <div className="text-sm text-slate-400 bg-white border border-slate-200 rounded-sm p-4 text-center">No requests submitted yet.</div>}
          {history.map((r) => (
            <div key={r.id} className="bg-white border border-slate-200 rounded-sm p-3 text-sm" data-testid={`portal-req-history-${r.id}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono text-xs text-[#1F7A6E] font-semibold">{r.request_no}</span>
                <span className={`tag ${STATUS_COLOR[r.status]}`}>{r.status}</span>
              </div>
              <div className="font-display font-bold">{r.grade} · {r.qty_m3} m³</div>
              <div className="text-xs text-slate-500">Requested for {r.requested_date} · {r.site_address}</div>
              <div className="text-[11px] text-slate-400 mt-1">Submitted {formatISTDateTime(r.created_at)}</div>
              {r.order ? (
                <div className="text-xs text-slate-600 mt-1 border-t border-slate-100 pt-1">
                  Order <span className="font-mono text-[#1F7A6E]">{r.order.order_no}</span> — {r.order.delivered_qty} / {r.order.contracted_qty} m³ delivered
                  {r.order.pending_qty > 0 ? `, ${r.order.pending_qty} m³ pending` : ' · complete'}
                </div>
              ) : r.quotation ? (
                <div className="text-xs text-slate-600 mt-1 border-t border-slate-100 pt-1">
                  Quotation <span className="font-mono text-[#1F7A6E]">{r.quotation.quotation_no}</span> sent — awaiting order
                </div>
              ) : r.status !== 'pending' && r.staff_notes ? (
                <div className="text-xs text-slate-600 mt-1 border-t border-slate-100 pt-1">ELR: {r.staff_notes}</div>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
